/**
 * Product Grid/Carousel Widget - Modern Vanilla JavaScript with Store API
 *
 * High-performance implementation with zero jQuery dependency.
 * Built with 2025 best practices using WooCommerce Store API.
 *
 * Performance Features:
 * - Zero jQuery (vanilla JavaScript only)
 * - Event delegation for efficiency
 * - Async/await for REST operations
 * - Modern Fetch API
 * - WooCommerce Store API (REST endpoints)
 *
 * @package Storedash_Checkout
 * @version 3.0.1
 * @updated 2025-10-13 - Store API migration (item_count fix)
 *
 * References:
 * - WooCommerce: /woocommerce/woocommerce - Store API cart endpoints
 * - Store API Docs: /docs/apis/store-api/resources-endpoints/cart.md
 * - MDN Web Docs: Modern JavaScript, Fetch API
 */

(function() {
    'use strict';

    // Configuration from WordPress
    const config = window.storedashProductGrid || {};

    /**
     * Product Grid Controller
     * Handles both grid and carousel modes
     */
    class ProductGridController {
        constructor(element) {
            
            if (!element) {
                return;
            }

            // CRITICAL: Prevent duplicate initialization
            if (element._storedashProductGridInitialized) {
                return;
            }
            element._storedashProductGridInitialized = true;

            this.element = element;

            // Find the products wrapper inside the widget
            this.productsWrapper = element.querySelector('.storedash-products-wrapper');
            if (!this.productsWrapper) {
                return;
            }

            this.carousel = element.querySelector('.storedash-products-carousel');
            this.isCarousel = !!this.carousel;
            this.swiper = null;
            this.isLoading = false;

            // BULLETPROOF FIX: Animation request tracking to prevent overlapping updates
            this.lastRequestId = 0;      // Track request sequence
            // updateTimeout removed - using requestAnimationFrame with requestId checks instead
            this.isUpdating = false;      // Prevent concurrent updates

            // Store reference on element for cross-widget access
            element._storedashProductGridController = this;

            // Bind methods to preserve context
            this.handleAddToCart = this.handleAddToCart.bind(this);
            this.handleWooCommerceEvent = this.handleWooCommerceEvent.bind(this);
            
            // Create arrow function wrapper for filter event to preserve context
            this.filterEventHandler = (event) => {
                this.handleFiltersChanged(event);
            };

            // Debug log
            const buttons = element.querySelectorAll('.ajax_add_to_cart');
            // Initialize
            this.init();
        }

        /**
         * Initialize widget
         */
        init() {
            if (this.isCarousel) {
                this.initCarousel();
            }

            this.bindEvents();
            this.applyResponsiveTitleTruncation();

            // PERF FIX: Use shared resize broadcaster if available (already debounced)
            this._resizeCallback = () => this.applyResponsiveTitleTruncation();
            if (window.StoredashResize) {
                window.StoredashResize.on(this._resizeCallback);
            } else {
                let resizeTimeout;
                window.addEventListener('resize', () => {
                    clearTimeout(resizeTimeout);
                    resizeTimeout = setTimeout(this._resizeCallback, 150);
                });
            }
        }

        /**
         * Initialize Swiper carousel
         */
        initCarousel() {
            // Wait for Swiper to be available
            const initSwiper = () => {
                if (typeof Swiper === 'undefined') {
                    return;
                }

                // Get settings from data attribute
                const settings = this.carousel.dataset.settings
                    ? JSON.parse(this.carousel.dataset.settings)
                    : {};

                // Check if bleed is enabled for current breakpoint
                const isBleedEnabled = this.isBleedEnabledForBreakpoint(settings);
                
                // Apply bleed class if enabled
                if (isBleedEnabled) {
                    this.carousel.classList.add('carousel-bleed-enabled');
                    // Set CSS variable for gap to use in flush positioning
                    const gap = settings.spaceBetween || 20;
                    this.carousel.style.setProperty('--swiper-gap', gap + 'px');
                } else {
                    this.carousel.classList.remove('carousel-bleed-enabled');
                }

                // CRITICAL: Find navigation/pagination elements in the widget container
                // They can be either inside carousel OR in header (when integrated with heading)
                const nextButton = this.element.querySelector('.swiper-button-next');
                const prevButton = this.element.querySelector('.swiper-button-prev');
                const paginationEl = this.element.querySelector('.swiper-pagination');

                const isMobile = window.innerWidth < 768;

                const swiperConfig = {
                    slidesPerView: settings.slidesPerView || 2,
                    spaceBetween: settings.spaceBetween || 20,
                    loop: settings.loop || false,
                    speed: isMobile ? 400 : 600,
                    grabCursor: true,

                    // Touch/Click handling - increase threshold to better distinguish clicks from swipes
                    threshold: 15, // Minimum distance (px) to trigger swipe - generous for clean taps on touch

                    // Autoplay
                    autoplay: settings.autoplay || false,

                    // Navigation - IMPORTANT: Scope to THIS widget instance only
                    navigation: settings.navigation && nextButton && prevButton ? {
                        nextEl: nextButton,
                        prevEl: prevButton,
                    } : false,

                    // Pagination - IMPORTANT: Scope to THIS widget instance only
                    pagination: settings.pagination && paginationEl ? {
                        el: paginationEl,
                        clickable: true,
                    } : false,

                    // Breakpoints for responsive
                    breakpoints: settings.breakpoints || {},

                    // Carousel bleed settings
                    watchOverflow: !isBleedEnabled, // Disable when bleeding
                    centeredSlides: false, // CRITICAL: Never center slides when bleed is enabled
                    slidesOffsetBefore: 0, // Start from left edge
                    slidesOffsetAfter: 0,

                    // Performance: skip slide progress/visibility tracking on mobile freeMode (smoother scroll)
                    watchSlidesProgress: !isMobile,
                    watchSlidesVisibility: !isMobile,
                    preloadImages: false,
                    lazy: {
                        loadPrevNext: true,
                        loadPrevNextAmount: 2,
                    },

                    // Observers for dynamic content (slide children off on mobile — less work during drag)
                    observer: true,
                    observeParents: true,
                    observeSlideChildren: !isMobile,
                };

                // Enable freeMode on mobile when setting is on (default: true for backwards compat)
                if (isMobile && settings.mobileFreeMode !== false) {
                    swiperConfig.freeMode = true;
                    swiperConfig.touchReleaseOnEdges = true;
                    swiperConfig.threshold = 0;
                    swiperConfig.touchStartPreventDefault = false;
                    swiperConfig.resistanceRatio = 0.85;
                    swiperConfig.loop = false;
                }

                // Initialize Swiper
                try {
                    this.swiper = new Swiper(this.carousel, swiperConfig);
                    
                    // === Click vs Drag handling ===
                    // Uses pointer events to reliably distinguish taps from swipes.
                    // On touch devices, Swiper may suppress native click events,
                    // so we detect taps via pointerup and programmatically click.
                    let pointerStartX = 0;
                    let pointerStartY = 0;
                    let wasDragged = false;
                    const DRAG_THRESHOLD = 10;

                    this.carousel.addEventListener('pointerdown', (e) => {
                        pointerStartX = e.clientX;
                        pointerStartY = e.clientY;
                        wasDragged = false;
                    }, { passive: true });

                    this.carousel.addEventListener('pointermove', (e) => {
                        if (!wasDragged) {
                            const dx = Math.abs(e.clientX - pointerStartX);
                            const dy = Math.abs(e.clientY - pointerStartY);
                            if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
                                wasDragged = true;
                            }
                        }
                    }, { passive: true });

                    // Prevent click when dragging (capture phase intercepts before links)
                    this.carousel.addEventListener('click', (e) => {
                        if (wasDragged) {
                            e.preventDefault();
                            e.stopPropagation();
                            wasDragged = false;
                        }
                    }, true);

                    // Touch tap fallback: Swiper may prevent native click on touch,
                    // so detect taps and programmatically trigger the click
                    this.carousel.addEventListener('pointerup', (e) => {
                        if (e.pointerType !== 'touch' || wasDragged) return;

                        const dx = Math.abs(e.clientX - pointerStartX);
                        const dy = Math.abs(e.clientY - pointerStartY);

                        if (dx < DRAG_THRESHOLD && dy < DRAG_THRESHOLD) {
                            const interactive = e.target.closest('a[href], button, [role="button"]');
                            if (interactive) {
                                // Defer to next microtask to avoid conflicts with Swiper
                                Promise.resolve().then(() => interactive.click());
                            }
                        }
                    }, { passive: true });
                    
                    // Handle responsive bleed on resize
                    // PERFORMANCE: Use requestAnimationFrame debounce to prevent thrashing
                    let bleedResizeRaf = null;
                    window.addEventListener('resize', () => {
                        if (bleedResizeRaf) return; // Skip if a frame is already scheduled
                        bleedResizeRaf = requestAnimationFrame(() => {
                            bleedResizeRaf = null;
                            const newBleedState = this.isBleedEnabledForBreakpoint(settings);
                            if (newBleedState && !this.carousel.classList.contains('carousel-bleed-enabled')) {
                                this.carousel.classList.add('carousel-bleed-enabled');
                            } else if (!newBleedState && this.carousel.classList.contains('carousel-bleed-enabled')) {
                                this.carousel.classList.remove('carousel-bleed-enabled');
                            }
                        });
                    });
                } catch (error) {
                }
            };

            // Check if Swiper is already loaded
            if (typeof Swiper !== 'undefined') {
                initSwiper();
            } else {
                // PERFORMANCE: Use exponential backoff instead of fixed 100ms polling
                // Faster detection for quick loads (16ms initial), still works for slow loads
                // Max wait: ~2.5 seconds (vs 5 seconds before)
                const waitForSwiper = (delay = 16, maxDelay = 1000, totalWait = 0, maxWait = 2500) => {
                    if (typeof Swiper !== 'undefined') {
                        initSwiper();
                        return;
                    }
                    if (totalWait >= maxWait) {
                        // Swiper never loaded - fail silently
                        return;
                    }
                    // Exponential backoff: 16 -> 32 -> 64 -> 128 -> 256 -> 512 -> 1000 (capped)
                    const nextDelay = Math.min(delay * 2, maxDelay);
                    setTimeout(() => waitForSwiper(nextDelay, maxDelay, totalWait + delay, maxWait), delay);
                };
                waitForSwiper();
            }
        }

        /**
         * Check if bleed is enabled for current breakpoint
         */
        isBleedEnabledForBreakpoint(settings) {
            if (!settings.bleedEnabled) return false;
            
            const width = window.innerWidth;
            
            if (width >= 1024) {
                return settings.bleedEnabled.desktop || false;
            } else if (width >= 768) {
                return settings.bleedEnabled.tablet || false;
            } else {
                return settings.bleedEnabled.mobile || false;
            }
        }

        /**
         * Apply responsive title truncation based on viewport width
         * Uses Elementor breakpoints: desktop >= 1024px, tablet >= 768px, mobile < 768px
         */
        applyResponsiveTitleTruncation() {
            if (!this.element) return;

            const width = window.innerWidth;
            let maxLength;
            let breakpoint;

            // Determine breakpoint and max length
            if (width >= 1024) {
                breakpoint = 'desktop';
            } else if (width >= 768) {
                breakpoint = 'tablet';
            } else {
                breakpoint = 'mobile';
            }

            // Find all product titles in this widget
            const titles = this.element.querySelectorAll('.product-title[data-full-title]');

            titles.forEach(titleEl => {
                const fullTitle = titleEl.getAttribute('data-full-title');
                if (!fullTitle) return;

                // Get the appropriate max length for current breakpoint
                const maxLengthDesktop = parseInt(titleEl.getAttribute('data-max-length-desktop')) || 0;
                const maxLengthTablet = parseInt(titleEl.getAttribute('data-max-length-tablet')) || maxLengthDesktop;
                const maxLengthMobile = parseInt(titleEl.getAttribute('data-max-length-mobile')) || maxLengthTablet;

                switch (breakpoint) {
                    case 'desktop':
                        maxLength = maxLengthDesktop;
                        break;
                    case 'tablet':
                        maxLength = maxLengthTablet;
                        break;
                    case 'mobile':
                        maxLength = maxLengthMobile;
                        break;
                    default:
                        maxLength = maxLengthDesktop;
                }

                // Get the link element inside the title
                const linkEl = titleEl.querySelector('a');
                if (!linkEl) return;

                // Apply truncation
                if (maxLength > 0 && fullTitle.length > maxLength) {
                    linkEl.textContent = fullTitle.substring(0, maxLength) + '...';
                } else {
                    linkEl.textContent = fullTitle;
                }
            });
        }

        /**
         * Bind events using event delegation
         */
        bindEvents() {
            
            // Use event delegation on the productsWrapper for better performance
            // and to handle dynamically added products
            this.clickHandler = (e) => {
                const addToCartBtn = e.target.closest('.ajax_add_to_cart');
                if (addToCartBtn) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleAddToCart(addToCartBtn);
                    return;
                }

                if (e.target.closest('.product-wishlist-heart')) {
                    return;
                }

                // Overlay .product-card-link uses pointer-events: none so hearts/links/buttons receive
                // real pointer events; navigate when the click is on non-interactive card chrome.
                const card = e.target.closest('.product-card');
                if (!card || !this.productsWrapper.contains(card)) {
                    return;
                }
                if (e.target.closest('button, a, input, select, textarea, [role="button"], label')) {
                    return;
                }
                const cardLink = card.querySelector('.product-card-link');
                if (!cardLink || !cardLink.href) {
                    return;
                }
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                    window.open(cardLink.href, '_blank', 'noopener,noreferrer');
                    return;
                }
                window.location.assign(cardLink.href);
            };

            // Bind to the products wrapper
            this.productsWrapper.addEventListener('click', this.clickHandler);

            // Also test: check if buttons exist
            const testButtons = this.element.querySelectorAll('.ajax_add_to_cart');

            // WooCommerce events
            this.bindWooCommerceEvents();
        }

        /**
         * Bind WooCommerce events for cart updates
         */
        bindWooCommerceEvents() {
            // Modern WooCommerce Blocks events
            document.body.addEventListener('wc-blocks_added_to_cart', this.handleWooCommerceEvent);
            document.body.addEventListener('wc-blocks_removed_from_cart', this.handleWooCommerceEvent);

            // Legacy WooCommerce events (backwards compatibility)
            document.body.addEventListener('added_to_cart', this.handleWooCommerceEvent);
            document.body.addEventListener('removed_from_cart', this.handleWooCommerceEvent);

            // NATIVE CustomEvent listener for filter changes (100% vanilla JS)
            // This connects to the blazing-fast custom filter tables backend
            // Use arrow function wrapper to preserve 'this' context
            document.addEventListener('storedash:filters:changed', this.filterEventHandler);

            // Show skeleton loaders as soon as filters start loading (before AJAX completes)
            this.filterLoadingHandler = () => this.showFilterLoading();
            document.addEventListener('storedash:filters:loading', this.filterLoadingHandler);
            
            // NOTE: Sort widget now dispatches 'storedash:filters:changed' event
            // So we don't need separate sort event listeners!
            // Card density is handled via CSS-only by sort widget
        }

    /**
     * Handle filter changes - update product grid without page refresh
     * Native CustomEvent handler - data is in event.detail
     */
    async handleFiltersChanged(event) {
        
        // Extract data from native CustomEvent detail
        const data = event.detail || {};

        // Skip test events
        if (data.test) {
            return;
        }

        // CRITICAL FIX: Handle cleared filters
        // When filters are cleared, we should load all products (no page reload needed!)
        if (data.cleared) {
            const hasProductsHtml = typeof data.products_html === 'string' && data.products_html.length > 0;
            const hasProductIds = Array.isArray(data.productIds) && data.productIds.length > 0;

            // Some clear flows dispatch a state-sync event before the AJAX payload arrives.
            // Ignore incomplete clear events so we do not blank the product grid.
            if (!hasProductsHtml && !hasProductIds) {
                this.hideFilterLoading();
                return;
            }
        }
        
        // NEW: Handle Sort Widget events (they provide orderby/order but not products_html yet)
        // But skip if this is a pagination update event (fromSortComplete flag)
        if (data.fromSort && data.orderby && data.order && !data.fromSortComplete) {
            await this.handleSortChange(data.filters, data.context, data.orderby, data.order);
            return;
        }
        
        // Skip sort completion events (they're just for pagination)
        if (data.fromSortComplete) {
            return;
        }
        
        // Check if no products match filters (but allow cleared state to pass through)
        // When filters are cleared, we should have products_html with all products
        if (!data || (!data.cleared && (!data.productIds || data.productIds.length === 0))) {
            // Hide all products (show "no products found" message)
            this.filterProductsInDOM([]);
            this.hideFilterLoading();
            return;
        }

        try {
            await this.loadFilteredProducts(
                data.productIds,
                data.context,
                data.products_html,
                data.products,
                data.widget_settings,
                data.i18n
            );

        } catch (error) {
            console.warn('Storedash: Product grid update failed', error.message);
        } finally {
            this.hideFilterLoading();
        }
    }

    /**
     * Get widget settings from product grid data attribute
     * Same method as in filters.js for consistency
     */
    getProductGridSettings() {
        // Try to get settings from this widget's wrapper first
        const productsWrapper = this.productsWrapper;
        
        if (productsWrapper && productsWrapper.dataset.settings) {
            try {
                const settings = JSON.parse(productsWrapper.dataset.settings);
                return settings;
            } catch (e) {
            }
        }

        // Fallback defaults
        return {
            show_image: 'yes',
            show_title: 'yes',
            show_price: 'yes',
            show_add_to_cart: 'yes',
            show_sale_badge: 'yes',
            sale_badge_content_type: 'text',
            image_size: 'woocommerce_thumbnail',
        };
    }

    /**
     * Handle sort changes from Sort By widget
     * Makes AJAX call with orderby/order parameters
     */
    async handleSortChange(filters, context, orderby, order) {
        if (!window.storedashFiltersData) {
            return;
        }

        // PERF: Clear AJAX result cache — sort order changed, cached results are stale
        if (window.StoredashFilterCache) {
            window.StoredashFilterCache.clear();
        }

        // VALIDITY GATE for client-side sort: reordering DOM cards is only
        // correct when the cards in the DOM are the COMPLETE result set. If
        // more server pages exist (load-more not exhausted, or numbered
        // pagination), page 1 of the NEW global ordering must be fetched from
        // the server — the truly cheapest/newest/etc. product may not be
        // loaded at all. Without this gate, "cheapest first" showed the
        // cheapest of the loaded batch only, and load-more then appended
        // globally-ranked pages that didn't line up with the partial
        // client-side reorder (duplicates eaten by the dedup net, expensive
        // items above cheap ones).
        const sortPaginationWrapper = document.querySelector('.storedash-pagination-wrapper');
        let allResultsLoaded = true;
        if (sortPaginationWrapper) {
            const maxPages = parseInt(sortPaginationWrapper.getAttribute('data-max-pages')) || 1;
            const curPage = parseInt(sortPaginationWrapper.getAttribute('data-current-page')) || 1;
            // load_more / infinite_scroll ACCUMULATE pages in the DOM;
            // numbered pagination shows only one page at a time.
            const accumulates = ['load_more', 'infinite_scroll'].includes(sortPaginationWrapper.dataset.type);
            allResultsLoaded = maxPages <= 1 || (accumulates && curPage >= maxPages);
        }

        // PERF: Try client-side sort using descriptor map (instant, no AJAX).
        // Only valid when the full result set is already in the DOM (gate above).
        const descriptorMap = window.StoredashDescriptorMap;
        if (allResultsLoaded && descriptorMap && descriptorMap.canSort()) {
            const grid = this.productsWrapper.querySelector('.storedash-products-grid');
            if (grid) {
                const cards = Array.from(grid.querySelectorAll('.product-card[data-product-id]'));
                if (cards.length > 0) {
                    const currentIds = cards.map(c => parseInt(c.dataset.productId, 10));
                    // Explicit user sort (this method only runs from a sort-widget
                    // click) — pure ordering, no push-OOS grouping. Matches the
                    // server's explicit-sort behavior in FilterAPI.
                    const sortedIds = descriptorMap.sortProductIds(currentIds, orderby, order, false);

                    if (sortedIds) {
                        // Build lookup: productId → card element
                        const cardMap = {};
                        cards.forEach(c => { cardMap[c.dataset.productId] = c; });

                        // Reorder DOM nodes (no re-render, no AJAX)
                        const fragment = document.createDocumentFragment();
                        sortedIds.forEach(id => {
                            const card = cardMap[id];
                            if (card) fragment.appendChild(card);
                        });
                        grid.appendChild(fragment);

                        // Preserve existing pagination state — we only reordered current page.
                        // Read real totals from the pagination wrapper (set by last server response).
                        const paginationWrapper = document.querySelector('.storedash-pagination-wrapper');
                        const perPage = parseInt(window.storedashFiltersData.perPage) || 12;
                        const existingTotal = paginationWrapper
                            ? parseInt(paginationWrapper.getAttribute('data-max-pages')) || 1
                            : 1;
                        // Client-side sort only reorders already-loaded cards —
                        // report the real current page so load-more continues
                        // from where it was instead of resetting to page 1.
                        const existingCurrent = paginationWrapper
                            ? parseInt(paginationWrapper.getAttribute('data-current-page')) || 1
                            : 1;

                        const updateEvent = new CustomEvent('storedash:filters:changed', {
                            detail: {
                                filters: filters,
                                context: context,
                                totalProducts: existingTotal * perPage, // Approximate from page count
                                totalPages: existingTotal,
                                currentPage: existingCurrent,
                                perPage: perPage,
                                productIds: sortedIds,
                                fromSortComplete: true,
                            },
                            bubbles: true
                        });
                        document.dispatchEvent(updateEvent);
                        return; // Done — no AJAX needed
                    }
                }
            }
        }

        // Fallback: AJAX sort (title sort, missing descriptors, carousel mode, etc.)
        this.showFilterLoading();

        try {

            // Get widget settings for consistent rendering
            const widgetSettings = this.getProductGridSettings();

            const response = await fetch(window.storedashFiltersData.restUrl + 'storedash/v1/ajax-filter', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': window.storedashFiltersData.nonce || '',
                },
                body: JSON.stringify({
                    filters: filters,
                    context: context,
                    paged: 1,
                    // Same per_page chain as pagination.js loadMore() — a mismatch
                    // would shift the page-2 offset and skip/repeat products
                    per_page: widgetSettings.products_per_page || window.storedashFiltersData.perPage || 12,
                    orderby: orderby,
                    order: order,
                    widget_settings: widgetSettings, // CRITICAL: Pass settings for proper rendering
                })
            });

            if (!response.ok) {
                throw new Error('Sort request failed');
            }

            const data = await response.json();

            if (data.success) {
                await this.loadFilteredProducts(
                    data.product_ids || [],
                    context,
                    data.products_html,
                    data.products,
                    data.widget_settings,
                    data.i18n
                );

                // Dispatch event for pagination to update its state
                // Pagination widget needs totalPages to show/hide buttons
                // Use fromSortComplete flag to prevent infinite loop
                const updateEvent = new CustomEvent('storedash:filters:changed', {
                    detail: {
                        filters: filters,
                        context: context,
                        totalProducts: data.total_products || 0,
                        totalPages: data.total_pages || 1,
                        currentPage: data.current_page || 1,
                        perPage: data.per_page || 12,
                        productIds: data.product_ids || [],
                        fromSortComplete: true, // Flag to prevent Product Grid from handling this again
                    },
                    bubbles: true
                });
                document.dispatchEvent(updateEvent);
            }
        } catch (error) {
        } finally {
            this.hideFilterLoading();
        }
    }

        /**
         * Load filtered products and re-render
         * BULLETPROOF FIX: Request ID tracking prevents overlapping animations
         */
        async loadFilteredProducts(productIds, context, productsHTML, productsData = [], widgetSettings = {}, i18n = {}) {
            // BULLETPROOF FIX: Generate unique request ID
            // Stale requests are cancelled via requestId check in rAF callback
            const requestId = ++this.lastRequestId;

            this.isUpdating = true;

            // Find the grid container to replace
            const grid = this.productsWrapper.querySelector('.storedash-products-grid');
            const carousel = this.productsWrapper.querySelector('.swiper-wrapper');
            const targetContainer = this.isCarousel ? carousel : grid;

            if (!targetContainer) {
                this.isUpdating = false;
                return;
            }

            // CRITICAL FIX: Replace entire grid HTML with fresh filtered products
            const rendererAvailable = typeof window.StoredashProductRenderer !== 'undefined';
            const fallbackHTML = rendererAvailable
                ? window.StoredashProductRenderer.renderProducts(productsData || [], widgetSettings || {}, i18n || {})
                : '';

            const finalHTML = productsHTML || fallbackHTML;

            if (finalHTML) {

                // PERFORMANCE FIX: Removed artificial 200ms delay
                // Use requestAnimationFrame for minimal delay while allowing smooth transitions
                targetContainer.style.transition = 'opacity 0.15s ease-out';
                targetContainer.style.opacity = '0.3';

                // Use double-rAF to ensure opacity change is painted before content swap
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // BULLETPROOF: Check if this is still the latest request
                        if (requestId !== this.lastRequestId) {
                            this.isUpdating = false;
                            return;
                        }

                        // Replace HTML
                        targetContainer.innerHTML = finalHTML;

                        // Fade in immediately
                        targetContainer.style.opacity = '1';

                        // Reinitialize carousel if needed
                        if (this.isCarousel && this.swiper) {
                            this.updateSwiper();
                        }

                        // Apply responsive title truncation to newly loaded products
                        this.applyResponsiveTitleTruncation();

                        this.isUpdating = false;
                    });
                });
            } else {
                this.filterProductsInDOM(productIds);
                this.isUpdating = false;
            }
        }

        /**
         * BULLETPROOF: Enhanced Swiper update with error handling
         */
        updateSwiper() {
            if (!this.swiper) return;

            try {
                const oldSlideCount = this.swiper.slides.length;

                // Stop autoplay during update
                if (this.swiper.autoplay && this.swiper.autoplay.running) {
                    this.swiper.autoplay.stop();
                }

                // Reset to first slide if drastic change
                const newHTML = this.productsWrapper.innerHTML;
                const estimatedNewSlides = (newHTML.match(/swiper-slide/g) || []).length;

                if (Math.abs(oldSlideCount - estimatedNewSlides) > 10) {
                    this.swiper.slideTo(0, 0); // Instant jump to first slide
                }

                // Update Swiper
                this.swiper.update();

                // Restart autoplay if it was running
                const settings = this.carousel?.dataset.settings ? JSON.parse(this.carousel.dataset.settings) : {};
                if (settings.autoplay) {
                    this.swiper.autoplay.start();
                }

                // Verify after update
                setTimeout(() => {
                    if (this.swiper && this.swiper.slides.length === 0) {
                        this.swiper.destroy(true, true);
                        this.initCarousel();
                    }
                }, 100);
            } catch (error) {
            }
        }

        /**
         * Filter products in DOM (quick method)
         */
        filterProductsInDOM(productIds) {
            
            // Select by data-product-id attribute (works with any product markup)
            const products = this.productsWrapper.querySelectorAll('[data-product-id]');
            
            let visibleCount = 0;
            let hiddenCount = 0;
            
            products.forEach(product => {
                // Since we selected by [data-product-id], we know it exists
                const productId = product.dataset.productId;

                if (productId && productIds.includes(parseInt(productId))) {
                    product.style.display = '';
                    product.style.opacity = '1';
                    visibleCount++;
                } else if (productId) {
                    product.style.display = 'none';
                    product.style.opacity = '0';
                    hiddenCount++;
                }
            });
            
            // Apply responsive title truncation after filtering
            this.applyResponsiveTitleTruncation();
        }

        /**
         * Show loading state with skeleton
         * PERFORMANCE: Shows skeleton placeholder during AJAX filter operations
         */
        showFilterLoading() {
            // Add filter-loading class to show skeleton and hide products
            this.productsWrapper.classList.add('filter-loading');
            this.productsWrapper.classList.remove('loaded');
            this.productsWrapper.style.pointerEvents = 'none';
        }

        /**
         * Hide loading state and show products
         * PERFORMANCE: Hides skeleton and reveals loaded products
         */
        hideFilterLoading() {
            // Remove filter-loading class to hide skeleton and show products
            this.productsWrapper.classList.remove('filter-loading');
            this.productsWrapper.classList.add('loaded');
            this.productsWrapper.style.pointerEvents = '';
        }

        /**
         * Show all products (called when filters are cleared)
         */
        showAllProducts() {
            
            // Select all products in the wrapper
            const products = this.productsWrapper.querySelectorAll('[data-product-id]');
            
            let restoredCount = 0;
            
            // Show all products
            products.forEach(product => {
                product.style.display = '';
                product.style.opacity = '1';
                restoredCount++;
            });

            // Reinitialize carousel if needed
            if (this.isCarousel && this.swiper) {
                this.swiper.update();
            }
        }

        /**
         * Handle WooCommerce cart update events
         */
        handleWooCommerceEvent(event) {
            // Optional: Update product buttons if needed
            // For now, just log the event
        }

        /**
         * Handle add to cart button click using Store API
         */
        async handleAddToCart(button) {
            if (!button || button.disabled || button.classList.contains('disabled') || button.classList.contains('out-of-stock')) {
                return;
            }

            if (this.isLoading || button.classList.contains('loading')) {
                return;
            }

            const productId = button.dataset.product_id;
            const quantity = parseInt(button.dataset.quantity) || 1;

            if (!productId) {
                return;
            }

            // Update button state
            button.classList.add('loading');
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
            this.isLoading = true;

            try {
                // Use WooCommerce Store API (modern REST endpoint)
                const storeApiUrl = config.storeApiUrl + 'cart/add-item';

                const response = await fetch(storeApiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Nonce': config.storeApiNonce,
                        // Explicit no-cache for LiteSpeed/WP Rocket/Cloudflare compatibility
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                    },
                    body: JSON.stringify({
                        id: parseInt(productId),
                        quantity: quantity,
                    }),
                    credentials: 'same-origin',
                });

                const data = await response.json();

                // Store API error handling
                if (!response.ok) {
                    const errorMessage = data.message || data.code || 'Error adding product to cart';
                    const error = new Error(errorMessage);
                    error.status = response.status;
                    if (data && typeof data.code === 'string') {
                        error.code = data.code;
                    }
                    if (data && data.data) {
                        error.data = data.data;
                    }
                    throw error;
                }

                // Success! Update button state
                button.classList.remove('loading');
                button.classList.add('added');
                button.disabled = false;
                button.removeAttribute('aria-disabled');

                // Dispatch events for other components
                this.triggerAddToCartEvents(data, button, productId);

                // Remove "View cart" links that WooCommerce might add
                setTimeout(() => {
                    const viewCartLinks = document.querySelectorAll('.added_to_cart.wc-forward');
                    viewCartLinks.forEach(link => link.remove());
                }, 100);

                // Reset button after 2 seconds
                setTimeout(() => {
                    button.classList.remove('added');
                }, 2000);

            } catch (error) {
                button.classList.remove('loading');

                if (this.isOutOfStockError(error)) {
                    this.markButtonOutOfStock(button);
                } else {
                    button.disabled = false;
                    button.removeAttribute('aria-disabled');
                    button.classList.remove('disabled');
                }

                if (typeof StoredashToast !== 'undefined' && typeof StoredashToast.errorFromResponse === 'function') {
                    StoredashToast.errorFromResponse(error);
                } else {
                    const errorMsg = error.message || config.i18n?.error || 'Error adding product to cart. Please try again.';
                    alert(errorMsg);
                }
            } finally {
                this.isLoading = false;
            }
        }

        /**
         * Mark button as permanently out of stock
         */
        markButtonOutOfStock(button) {
            if (!button) return;

            button.classList.remove('added');
            button.classList.add('disabled', 'out-of-stock');
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
        }

        /**
         * Determine if error relates to stock availability
         */
        isOutOfStockError(error) {
            const message = (error?.message || '').toLowerCase();
            const code = (error?.code || '').toLowerCase();

            if (!message && !code) {
                return false;
            }

            const stockIndicators = [
                'out of stock',
                'stock unavailable',
                'no longer available',
                'product_stock',
                'out_of_stock',
                'stock_exceeded',
            ];

            return stockIndicators.some(indicator => message.includes(indicator) || code.includes(indicator));
        }

        /**
         * Get WooCommerce Store API URL
         * 
         * Store API is the modern WooCommerce REST endpoint.
         * Available since WooCommerce 5.5+, stable in 6.0+, recommended for 7.0+
         * 
         * @return string Store API base URL
         */
        getStoreApiUrl() {
            if (config.storeApiUrl) {
                return config.storeApiUrl;
            }
            // Fallback to default REST endpoint
            return '/wp-json/wc/store/v1/';
        }

        /**
         * Trigger WooCommerce events for cart updates (Store API format)
         * 
         * Store API returns different data structure than legacy AJAX:
         * - data.items: Array of cart items
         * - data.totals: Cart totals object
         * - data.items_count: Total number of items
         * 
         * @param {Object} data - Store API response data
         * @param {HTMLElement} button - The add to cart button
         * @param {number} productId - Product ID that was added
         */
        triggerAddToCartEvents(data, button, productId) {
            // Modern CustomEvent with Store API data structure
            const eventDetail = {
                detail: {
                    // Store API response
                    cart: data,
                    items: data.items || [],
                    totals: data.totals || {},
                    itemCount: data.items_count || 0,
                    
                    // Additional context
                    button: button,
                    productId: productId,
                    
                    // CRITICAL: Flag to identify events from Storedash widgets
                    // This prevents the mini cart from intercepting WoodMart/other theme events
                    fromStoredash: true,
                },
            };

            // Dispatch modern native events
            document.body.dispatchEvent(new CustomEvent('added_to_cart', eventDetail));
            document.body.dispatchEvent(new CustomEvent('wc-blocks_added_to_cart', eventDetail));
            document.body.dispatchEvent(new CustomEvent('storedash_added_to_cart', eventDetail));

            // NOTE: We do NOT trigger wc_fragments_refresh with Store API
            // Store API uses a different pattern - the mini cart listens to events above
            // and updates itself via Store API, not via HTML fragments
        }

        /**
         * Destroy instance and cleanup
         */
        destroy() {
            if (this.swiper) {
                this.swiper.destroy(true, true);
                this.swiper = null;
            }

            // Remove click handler
            if (this.clickHandler && this.productsWrapper) {
                this.productsWrapper.removeEventListener('click', this.clickHandler);
            }

            // Remove WooCommerce event listeners
            document.body.removeEventListener('wc-blocks_added_to_cart', this.handleWooCommerceEvent);
            document.body.removeEventListener('wc-blocks_removed_from_cart', this.handleWooCommerceEvent);
            document.body.removeEventListener('added_to_cart', this.handleWooCommerceEvent);
            document.body.removeEventListener('removed_from_cart', this.handleWooCommerceEvent);
            
            // Remove filter event listeners
            if (this.filterEventHandler) {
                document.removeEventListener('storedash:filters:changed', this.filterEventHandler);
            }
            if (this.filterLoadingHandler) {
                document.removeEventListener('storedash:filters:loading', this.filterLoadingHandler);
            }

            // Cleanup shared resize listener
            if (this._resizeCallback && window.StoredashResize) {
                window.StoredashResize.off(this._resizeCallback);
            }
        }
    }

    /**
     * Builder-agnostic selector for product grid widgets
     * Matches: Elementor (.elementor-widget-*), Bricks (.brxe-*), or any future builder
     */
    const WIDGET_SELECTOR = '.elementor-widget-storedash-product-grid, .brxe-storedash-product-grid';

    /**
     * Initialize widgets on Elementor frontend
     */
    function initElementorWidget() {
        if (typeof elementorFrontend !== 'undefined' && elementorFrontend.hooks) {

            elementorFrontend.hooks.addAction('frontend/element_ready/storedash-product-grid.default', function($scope) {
                // $scope is a jQuery object in Elementor context, get native element
                const element = $scope[0] || $scope;
                new ProductGridController(element);
            });

            // IMPORTANT: Also initialize any widgets that are already on the page
            // This handles cases where the script loads after widgets are rendered
            const existingWidgets = document.querySelectorAll(WIDGET_SELECTOR);

            if (existingWidgets.length > 0) {
                existingWidgets.forEach((widget, index) => {
                    new ProductGridController(widget);
                });
            } else {
            }
        } else {
            // This is not an error - hooks may not be ready yet, or we're on a non-Elementor page
            // The fallback initialization will handle widgets just fine

            // Try to find widgets anyway and initialize them directly
            const existingWidgets = document.querySelectorAll(WIDGET_SELECTOR);

            if (existingWidgets.length > 0) {
                existingWidgets.forEach((widget, index) => {
                    new ProductGridController(widget);
                });
            }
        }
    }

    // Track if Elementor widget has been initialized to prevent duplicates
    let elementorWidgetInitialized = false;

    // Always wait for Elementor init event to ensure hooks are ready
    // This prevents the "hooks not available" error
    if (typeof elementorFrontend !== 'undefined') {
        // elementorFrontend exists, check if hooks are already available
        if (elementorFrontend.hooks) {
            initElementorWidget();
            elementorWidgetInitialized = true;
        } else {
            // hooks not ready yet, wait for init event
            window.addEventListener('elementor/frontend/init', () => {
                if (!elementorWidgetInitialized) {
                    initElementorWidget();
                    elementorWidgetInitialized = true;
                }
            });
        }
    } else {
        // Wait for Elementor to load
        window.addEventListener('elementor/frontend/init', () => {
            if (!elementorWidgetInitialized) {
                initElementorWidget();
                elementorWidgetInitialized = true;
            }
        });
    }

    /**
     * Initialize widgets on regular pages (non-Elementor, including Bricks)
     */
    function initProductGrids() {
        const widgets = document.querySelectorAll(WIDGET_SELECTOR);
        widgets.forEach((widget, index) => {
            new ProductGridController(widget);
        });

        if (widgets.length === 0) {
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            if (typeof elementorFrontend === 'undefined') {
                initProductGrids();
            } else {
            }
        });
    } else {
        // DOM already loaded
        if (typeof elementorFrontend === 'undefined') {
            initProductGrids();
        } else {
        }
    }

    // Expose controller for external use
    window.StoredashProductGridController = ProductGridController;

    /**
     * Bricks Builder editor integration
     * Called by Bricks via $scripts = ['storedashBricksProductGrid'] when the
     * element renders or re-renders in the editor (e.g., when settings change).
     * Destroys any previous instance and re-initializes with updated settings.
     */
    window.storedashBricksProductGrid = function(element) {
        if (!element) return;

        // Destroy previous instance if it exists (settings may have changed)
        const existing = element._storedashProductGridController;
        if (existing && typeof existing.destroy === 'function') {
            existing.destroy();
            delete element._storedashProductGridController;
            element._storedashProductGridInitialized = false;
        }

        new ProductGridController(element);
    };

    // BULLETPROOF FIX: Memory leak prevention with MutationObserver
    // Auto-cleanup when widgets are removed from DOM (eg. AJAX navigation)
    // PERF FIX: Scope observer to .elementor or #content instead of document.body
    if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.removedNodes.forEach((node) => {
                    // Check if removed node is a product grid widget
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const controller = node._storedashProductGridController;
                        if (controller && typeof controller.destroy === 'function') {
                            controller.destroy();
                            delete node._storedashProductGridController;
                        }

                        // Also check child widgets
                        if (node.querySelectorAll) {
                            node.querySelectorAll(WIDGET_SELECTOR).forEach(widget => {
                                const childController = widget._storedashProductGridController;
                                if (childController && typeof childController.destroy === 'function') {
                                    childController.destroy();
                                    delete widget._storedashProductGridController;
                                }
                            });
                        }
                    }
                });
            });
        });

        const observeTarget = document.querySelector('.elementor') || document.getElementById('content') || document.body;
        observer.observe(observeTarget, {
            childList: true,
            subtree: true
        });

    }

})();
